import { Dispatch, SetStateAction } from "react";
import { useNavigate } from "react-router-dom";

import { ArrowDownIcon } from "components/icons";
import NavItemChild from "../NavItemChild";

import { useTranslationContext } from "context/TranslationContext";
import cn from "lib/cn";

import { ENav, TNavItem } from "../../types";

import navStyle from "../NavItem/NavItem.module.css";
import styles from "./NavItemDropdown.module.css";

export interface NavItemDropdownProps {
  id?: string;
  collapsed: boolean;
  openedChildren?: ENav;
  setOpenedChildren: Dispatch<SetStateAction<ENav | undefined>>;
  setCollapsed: Dispatch<SetStateAction<boolean>>;
  nav: TNavItem;
  activeNav?: ENav;
  onClick?: (href: string, name: string) => void;
}

const NavItemDropdown = ({
  id,
  collapsed,
  nav,
  openedChildren,
  activeNav,
  setOpenedChildren,
  setCollapsed,
  onClick,
}: NavItemDropdownProps) => {
  const navigate = useNavigate();
  const { t } = useTranslationContext();

  const handleOnClickDropdownNav = (navItem: TNavItem) => {
    if (openedChildren === navItem.name) {
      setOpenedChildren(undefined);
      return;
    }
    setCollapsed(false);
    setOpenedChildren(navItem.name);
  };

  const handleClickShowMore = (href?: string) => {
    if (!href) return;
    navigate(href);
    setCollapsed(true);
  };

  return (
    <div id={id} data-testid={id} className="w-full">
      <div
        data-testid={id ? `${id}-button-nav` : undefined}
        onClick={() => handleOnClickDropdownNav(nav)}
        className={cn([
          "flex w-full cursor-pointer",
          "hover:bg-primary-800",
          (openedChildren === nav.name || activeNav === nav.name) &&
            "bg-primary-800 text-primary-300",
          navStyle.navItem,
        ])}
      >
        <div className="flex justify-between w-full items-center">
          <div className="flex">
            <div
              className={cn(
                navStyle.separator,
                activeNav === nav.name && navStyle.separatorActive
              )}
            />

            <div
              className={cn(
                "flex space-x-6 px-4 py-4",
                navStyle.navItemWrapper
              )}
            >
              <div
                className={cn(
                  "flex justify-center items-center",
                  (openedChildren === nav.name || activeNav === nav.name) &&
                    navStyle.menuActive
                )}
              >
                {nav.icon}
              </div>
              <p className="text-base inline-flex whitespace-pre break-keep overflow-hidden">
                {t(nav.label)}
              </p>
            </div>
          </div>

          <div className="mr-6">
            <ArrowDownIcon
              className={cn(
                styles.caret,
                openedChildren === nav.name ? styles.open : styles.close
              )}
              width={17}
              height={17}
            />
          </div>
        </div>
      </div>
      {!collapsed && openedChildren === nav.name && (
        <ul
          className={cn(
            "flex flex-col w-full py-3 overflow-hidden -z-10",
            styles.item
          )}
        >
          {nav.children?.map((child, idx) => (
            <NavItemChild
              id={id ? `${id}-nav-item-child-${idx}` : undefined}
              nav={child}
              key={idx}
              name={nav.name}
              setCollapsed={setCollapsed}
              onClick={onClick}
            />
          ))}
          {nav.showShowMore && nav.hrefShowMore && (
            <div
              onClick={() => handleClickShowMore(nav.hrefShowMore)}
              className="text-primary-300 text-sm pl-7 cursor-pointer"
            >
              {t("show_more")}
            </div>
          )}
        </ul>
      )}
    </div>
  );
};

export default NavItemDropdown;
